import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ReceiptText,
  IndianRupee,
  Search,
  Printer,
  Ban,
  MoreVertical,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { invoicesService } from '@/services/invoices';
import { fmt } from '@/lib/format';
import { printHtmlInWindow } from '@/lib/print';
import type { Invoice, InvoiceStatus } from '@/types/invoices';

type StatusFilter = 'all' | InvoiceStatus;

const STATUS_LABEL: Record<InvoiceStatus, string> = {
  DRAFT: 'Draft',
  ISSUED: 'Issued',
  CANCELLED: 'Cancelled',
  PAID: 'Paid',
  PARTIALLY_PAID: 'Partial',
};

const STATUS_TONE: Record<InvoiceStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  DRAFT: 'muted',
  ISSUED: 'warning',
  CANCELLED: 'destructive',
  PAID: 'success',
  PARTIALLY_PAID: 'success',
};

export function InvoicesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [density, setDensity] = useState<TableDensity>('compact');
  // Cancel-with-reason dialog state. Reason flows into the audit trail.
  const [cancellingInvoice, setCancellingInvoice] = useState<Invoice | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['invoices', { q: search }],
    queryFn: () =>
      invoicesService.list({ search: search.trim() || undefined, pageSize: 200 }),
  });

  const allRows = useMemo(() => data?.rows ?? [], [data]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: allRows.length,
      DRAFT: 0,
      ISSUED: 0,
      CANCELLED: 0,
      PAID: 0,
      PARTIALLY_PAID: 0,
    };
    for (const r of allRows) c[r.status] += 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return allRows;
    return allRows.filter((r) => r.status === filter);
  }, [allRows, filter]);

  const summary = useMemo(() => {
    const issued = allRows
      .filter((r) => r.status === 'ISSUED' || r.status === 'PARTIALLY_PAID')
      .reduce((s, r) => s + Number(r.grandTotal), 0);
    const paid = allRows
      .filter((r) => r.status === 'PAID' || r.status === 'PARTIALLY_PAID')
      .reduce((s, r) => s + Number(r.grandTotal), 0);
    return {
      issued,
      paid,
      cancelled: counts.CANCELLED,
    };
  }, [allRows, counts.CANCELLED]);

  const print = useMutation({
    mutationFn: (id: string) => invoicesService.print(id),
    onSuccess: (printable) => {
      const opened = printHtmlInWindow(printable.html, { width: 900, height: 1100 });
      if (!opened) {
        toast.error('Popup blocked. Allow popups to print invoices.');
        return;
      }
      toast.success('Invoice rendered');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not render invoice'),
  });

  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      invoicesService.cancel(id, reason || undefined),
    onSuccess: (saved) => {
      toast.success(`Invoice ${saved.invoiceNumber} cancelled.`);
      qc.invalidateQueries({ queryKey: ['invoices'] });
      setCancellingInvoice(null);
      setCancelReason('');
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not cancel invoice'),
  });

  const tabs: StatusTabOption[] = [
    { value: 'all',             label: 'All',       count: counts.all },
    { value: 'ISSUED',          label: 'Issued',    count: counts.ISSUED },
    { value: 'PARTIALLY_PAID',  label: 'Partial',   count: counts.PARTIALLY_PAID },
    { value: 'PAID',            label: 'Paid',      count: counts.PAID },
    { value: 'CANCELLED',       label: 'Cancelled', count: counts.CANCELLED },
  ];

  const columns: Column<Invoice>[] = [
    {
      key: 'invoice',
      header: 'Invoice',
      width: '200px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-mono text-[12px] font-medium text-foreground">
            {r.invoiceNumber}
          </div>
          <div className="font-mono text-[10.5px] text-muted-foreground">
            {r.salesOrder.orderNumber}
          </div>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Bill to',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-medium text-foreground">
            {r.billingSnapshot.customerName ?? '—'}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {r.billingSnapshot.city}, {r.billingSnapshot.state}
          </div>
        </div>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      width: '110px',
      cell: (r) => (
        <span className="text-[12.5px] text-muted-foreground">
          {fmt.date(r.invoiceDate)}
        </span>
      ),
    },
    {
      key: 'tax',
      header: 'Tax type',
      width: '110px',
      cell: (r) => (
        <Badge variant="muted" className="text-[10px]">
          {Number(r.totalIgst) > 0 ? 'IGST' : 'CGST+SGST'}
        </Badge>
      ),
    },
    {
      key: 'subtotal',
      header: 'Subtotal',
      align: 'right',
      width: '120px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums text-muted-foreground">
          {fmt.money(Number(r.subtotal), r.currency)}
        </span>
      ),
    },
    {
      key: 'tax',
      header: 'Tax',
      align: 'right',
      width: '110px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums text-muted-foreground">
          {fmt.money(Number(r.totalTax), r.currency)}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Grand total',
      align: 'right',
      width: '130px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] font-medium tabular-nums">
          {fmt.money(Number(r.grandTotal), r.currency)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '110px',
      cell: (r) => (
        <Badge variant={STATUS_TONE[r.status]} className="text-[10px]">
          {STATUS_LABEL[r.status]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '90px',
      hoverReveal: true,
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={() => print.mutate(r.id)}
                disabled={print.isPending}
              >
                <Printer className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Print</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                aria-label="Invoice actions"
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => print.mutate(r.id)}>
                <Printer className="h-3.5 w-3.5" />
                Print invoice
              </DropdownMenuItem>
              {r.status !== 'CANCELLED' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      setCancelReason('');
                      setCancellingInvoice(r);
                    }}
                    className="text-destructive focus:text-destructive"
                  >
                    <Ban className="h-3.5 w-3.5" />
                    Cancel invoice
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <ListPageHeader title="Invoices" count={counts.all} />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <KpiCard label="Issued value" value={fmt.inr(summary.issued)} tone="primary" icon={ReceiptText} />
          <KpiCard label="Collected" value={fmt.inr(summary.paid)} tone="success" icon={IndianRupee} />
          <KpiCard
            label="Cancelled"
            value={fmt.number(summary.cancelled)}
            tone={summary.cancelled ? 'destructive' : 'default'}
            icon={Ban}
          />
        </div>

        <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoice # …"
              className="h-8 pl-8 text-[13px]"
            />
          </div>
          <DensityToggle value={density} onChange={setDensity} />
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            density={density}
            emptyTitle={
              filter === 'all'
                ? 'No invoices yet'
                : `No ${STATUS_LABEL[filter as InvoiceStatus].toLowerCase()} invoices`
            }
            emptyDescription={
              filter === 'all'
                ? 'Generate the first invoice from a sales order detail.'
                : 'Try a different status filter.'
            }
          />
        )}
      </div>

      {/* Cancel-with-reason dialog. Mirrors the affordance on
          OrderDetailSheet so cancel UX is consistent across surfaces.
          Reason flows into the audit log. */}
      <Dialog
        open={!!cancellingInvoice}
        onOpenChange={(o) => !o && setCancellingInvoice(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Cancel invoice {cancellingInvoice?.invoiceNumber}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 px-1 pb-1 text-[13px] leading-relaxed">
            <p className="text-muted-foreground">
              Marks the invoice CANCELLED. The audit log keeps the original
              record; re-issue from the order's Invoices section.
            </p>
            <div>
              <label
                htmlFor="invCancelReason"
                className="mb-1 block text-[11.5px] font-medium text-foreground"
              >
                Reason (optional, audit trail)
              </label>
              <Textarea
                id="invCancelReason"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. Wrong line items / wrong customer address / GSTIN incorrect"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCancellingInvoice(null)}
              disabled={cancel.isPending}
            >
              Keep invoice
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() =>
                cancellingInvoice &&
                cancel.mutate({
                  id: cancellingInvoice.id,
                  reason: cancelReason.trim() || undefined,
                })
              }
              disabled={cancel.isPending}
            >
              <Ban className="h-3.5 w-3.5" />
              {cancel.isPending ? 'Cancelling…' : 'Cancel invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
